import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  Eye,
  FileText,
  Info,
  Plus,
  RotateCcw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CleanTextSummary, TypeId } from './api';
import { FAMILY_CLASS, TYPE_FAMILY, TYPE_ORDER, normalizeTypeId } from './placeholders';

/** The file name of the download: the source's name with `.md`. */
export function markdownName(sourceName: string): string {
  const base = sourceName.replace(/\.(pdf|docx|xlsx|pptx|html?|md|markdown|txt|csv)$/i, '');
  return `${base === '' ? 'testo' : base}.md`;
}

export function saveText(text: string, name: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** The summary's types, in the board's order, then any id this screen does not know. */
export function summaryTypes(
  counts: Readonly<Record<string, number>>,
): { id: string; count: number }[] {
  const byId = new Map<string, number>();
  for (const [id, count] of Object.entries(counts)) {
    if (count > 0) byId.set(normalizeTypeId(id), count);
  }
  const known = TYPE_ORDER.filter((id) => byId.has(id)).map((id) => ({ id, count: byId.get(id)! }));
  const unknown = [...byId].filter(([id]) => !TYPE_ORDER.includes(id as TypeId));
  return [...known, ...unknown.map(([id, count]) => ({ id, count }))];
}

export interface TypeChipsProps {
  readonly types: readonly { id: string; count: number }[];
  readonly chosen: TypeId | null;
  readonly typeLabel: (id: string) => string;
  readonly onChoose: (id: TypeId) => void;
}

export function TypeChips({ types, chosen, typeLabel, onChoose }: TypeChipsProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {types.map(({ id, count }) => {
        const known = TYPE_ORDER.includes(id as TypeId);
        const family = known ? TYPE_FAMILY[id as TypeId] : 'codici';
        return (
          <button
            key={id}
            type="button"
            aria-pressed={chosen === id}
            disabled={!known}
            onClick={() => onChoose(id as TypeId)}
            className={cn(
              'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
              FAMILY_CLASS[family],
              chosen === id ? 'border-[var(--focus)] font-bold' : 'border-transparent',
            )}
          >
            {typeLabel(id)} <b>{count}</b>
          </button>
        );
      })}
    </div>
  );
}

export interface StepperProps {
  readonly typeName: string;
  readonly stops: readonly { label?: string }[];
  readonly position: number;
  readonly onStep: (by: 1 | -1) => void;
}

export function OccurrenceStepper({ typeName, stops, position, onStep }: StepperProps) {
  const { t } = useTranslation('tools');
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="grow" aria-live="polite">
        {stops.length > 0
          ? t('cleanText.result.stepper', {
              type: typeName,
              n: position + 1,
              total: stops.length,
              placeholder: stops[position]?.label,
            })
          : t('cleanText.result.noOccurrence', { type: typeName })}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-9"
        aria-label={t('cleanText.result.previous')}
        disabled={stops.length === 0}
        onClick={() => onStep(-1)}
      >
        <ChevronUp aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-9"
        aria-label={t('cleanText.result.next')}
        disabled={stops.length === 0}
        onClick={() => onStep(1)}
      >
        <ChevronDown aria-hidden="true" />
      </Button>
    </div>
  );
}

export interface NoticesProps {
  readonly summary: CleanTextSummary;
  readonly typeLabel: (id: string) => string;
  readonly onReprocessLanguage: (language: 'it' | 'en') => void;
}

function Recheck({ summary, typeLabel }: Pick<NoticesProps, 'summary' | 'typeLabel'>) {
  const { t } = useTranslation('tools');
  const lowConfidence = Object.entries(summary.lowConfidence ?? {}).filter(([, n]) => n > 0);
  const ambiguous = summary.ambiguous ?? [];
  if (lowConfidence.length === 0 && ambiguous.length === 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-md bg-[var(--attention-tint)] p-2.5 text-[var(--attention-ink)]">
      <Eye aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <div className="space-y-1">
        <b>{t('cleanText.result.recheck')}</b>
        {lowConfidence.map(([id, count]) => (
          <p key={id}>
            {t('cleanText.result.lowConfidence', { count, type: typeLabel(normalizeTypeId(id)) })}
          </p>
        ))}
        {ambiguous.length > 0 && (
          <p>{t('cleanText.result.ambiguous', { placeholders: ambiguous.join(', ') })}</p>
        )}
      </div>
    </div>
  );
}

export function Notices({ summary, typeLabel, onReprocessLanguage }: NoticesProps) {
  const { t } = useTranslation('tools');
  const otherLanguage = summary.language === 'it' ? 'en' : 'it';
  return (
    <div className="space-y-2 text-sm">
      {summary.pagesNotRead > 0 && (
        <p className="flex items-start gap-2 rounded-md bg-[var(--attention-tint)] p-2.5 text-[var(--attention-ink)]">
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <span>{t('cleanText.result.pagesNotRead', { count: summary.pagesNotRead })}</span>
        </p>
      )}
      <Recheck summary={summary} typeLabel={typeLabel} />
      {(summary.warnings ?? []).map((code) => (
        <p key={code} className="flex items-start gap-2 text-muted-foreground">
          <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <span>
            {t(`cleanText.warnings.${code}`, { defaultValue: t('cleanText.warnings.other') })}
          </span>
        </p>
      ))}
      <p className="flex items-start gap-2 text-muted-foreground">
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <span>
          {t('cleanText.result.language', {
            language: t(`cleanText.options.languages.${summary.language}`),
          })}{' '}
          <button
            type="button"
            className="font-semibold text-link underline"
            onClick={() => onReprocessLanguage(otherLanguage)}
          >
            {t(`cleanText.result.reprocessAs.${otherLanguage}`)}
          </button>
        </span>
      </p>
    </div>
  );
}

export function MissedTerm({ onAddTerm }: { readonly onAddTerm: () => void }) {
  const { t } = useTranslation('tools');
  return (
    <p className="text-sm">
      {t('cleanText.result.missed')}{' '}
      <button
        type="button"
        className="inline-flex items-center gap-1 font-semibold text-link underline"
        onClick={onAddTerm}
      >
        <Plus aria-hidden="true" className="size-3.5" />
        {t('cleanText.result.addTerm')}
      </button>
    </p>
  );
}

export interface ActsProps {
  readonly copied: 'done' | 'failed' | null;
  readonly onCopy: () => void;
  readonly onDownload: () => void;
  readonly onNewDocument: () => void;
}

export function ResultActs({ copied, onCopy, onDownload, onNewDocument }: ActsProps) {
  const { t } = useTranslation('tools');
  return (
    // On a phone this block stays in reach above the shell's bottom bar. Sticky inside the content,
    // as the shell's own phone actions are: `.app-shell` is a size container, so a fixed bar would
    // be placed against the whole page, under the tab bar (#413). The reminder is always right above
    // the acts.
    <div
      data-testid="clean-text-acts"
      className="sticky bottom-[calc(80px+env(safe-area-inset-bottom,0px))] z-[15] space-y-2.5 border-t border-border bg-card px-4 py-2.5 sm:static sm:rounded-xl sm:border sm:p-4 lg:col-start-2 lg:row-start-2 lg:self-start"
    >
      <p className="flex items-start gap-2 text-sm">
        <AlertTriangle
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-[var(--attention-ink)]"
        />
        <span>
          <b>{t('cleanText.result.reminderStrong')}</b> {t('cleanText.result.reminder')}
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" className="h-[52px] grow-[2] sm:h-10" onClick={onCopy}>
          <Copy aria-hidden="true" />
          <span className="sm:hidden">{t('cleanText.result.copyShort')}</span>
          <span className="hidden sm:inline">{t('cleanText.result.copy')}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-[52px] grow sm:h-10"
          onClick={onDownload}
        >
          <Download aria-hidden="true" />
          {t('cleanText.result.download')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="hidden sm:inline-flex sm:h-10 sm:basis-full sm:justify-start"
          onClick={onNewDocument}
        >
          <RotateCcw aria-hidden="true" />
          {t('cleanText.result.newDocument')}
        </Button>
      </div>
      {copied && (
        <p role="status" className="text-sm text-muted-foreground">
          {t(copied === 'done' ? 'cleanText.result.copied' : 'cleanText.result.copyFailed')}
        </p>
      )}
    </div>
  );
}

export interface DocumentHeaderProps {
  readonly name: string;
  readonly pages: number | null;
  readonly mode: 'rendered' | 'raw';
  readonly onMode: (mode: 'rendered' | 'raw') => void;
}

export function DocumentHeader({ name, pages, mode, onMode }: DocumentHeaderProps) {
  const { t } = useTranslation('tools');
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
      <FileText aria-hidden="true" className="size-4.5" />
      <b>{name}</b>
      {pages !== null && (
        <span className="text-muted-foreground">
          · {t('cleanText.result.pages', { count: pages })}
        </span>
      )}
      <span className="grow" />
      <div
        role="group"
        aria-label={t('cleanText.result.viewLabel')}
        className="flex rounded-md border border-chip-border p-0.5"
      >
        {(['rendered', 'raw'] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            onClick={() => onMode(m)}
            className={cn(
              'rounded px-2.5 py-1 text-sm font-semibold',
              mode === m ? 'bg-selected text-selected-foreground' : 'text-muted-foreground',
            )}
          >
            {t(`cleanText.result.view.${m}`)}
          </button>
        ))}
      </div>
    </div>
  );
}
